import { fmtMoney, fmtNum } from "./api";

function esc(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function periodLabel(r) {
  return r.from === r.to ? r.from : `${r.from} to ${r.to}`;
}

function lotCell(r) {
  let s = esc(r.lot_number || "—");
  if (r.stack_code) {
    s += ` (${esc(r.stack_code)}${r.stack_active === false ? ", Inactive" : ""})`;
  }
  if (r.warehouse_name) s += `<div class="muted">${esc(r.warehouse_name)}</div>`;
  return s;
}

function buildRentBillHtml(data, { title = "Storage Rent Bill", billNumber, status } = {}) {
  const lines = (data.statement || []).length
    ? (data.statement || []).map((r) => `
        <tr>
          <td>${esc(periodLabel(r))}</td>
          <td class="mono">${lotCell(r)}</td>
          <td>${esc(r.bag_type_name || "—")}</td>
          <td class="num">${esc(fmtNum(r.bags_per_day ?? r.bags))}</td>
          <td class="num">${esc(r.days)}</td>
          <td class="num">${esc(fmtMoney(r.daily_rent))}</td>
          <td class="calc">${esc(r.calculation || "—")}</td>
          <td class="num">${esc(fmtMoney(r.amount))}</td>
        </tr>`).join("")
    : `<tr><td colspan="8" class="empty">No line detail · total ${esc(fmtMoney(data.amount))}</td></tr>`;

  const exclusions = (data.holding?.billed_exclusions || []).map(
    (x) => `${esc(x.bill_number)} (${esc(x.from)} to ${esc(x.to)})`,
  ).join("; ");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${esc(billNumber || title)}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: "Segoe UI", Arial, sans-serif;
      color: #1c1917;
      margin: 0;
      padding: 20px 24px;
      font-size: 12px;
      line-height: 1.4;
    }
    .header { text-align: center; margin-bottom: 20px; }
    .header h1 { font-size: 22px; margin: 0 0 4px; font-weight: 700; }
    .header .doc-title { color: #57534e; font-size: 13px; }
    .header .bill-no { font-family: monospace; font-size: 13px; font-weight: 600; color: #065f46; margin-top: 6px; }
    .header .status { display: inline-block; margin-top: 6px; padding: 2px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; background: #ecfdf5; color: #065f46; }
    .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 24px; margin-bottom: 20px; }
    .meta .label { color: #78716c; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
    .meta .value { font-weight: 600; margin-top: 2px; }
    table { width: 100%; border-collapse: collapse; margin-top: 4px; }
    th, td { border: 1px solid #e7e5e4; padding: 7px 8px; vertical-align: top; text-align: left; }
    th { background: #fafaf9; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; color: #57534e; }
    td.num, th.num { text-align: right; white-space: nowrap; }
    td.mono { font-family: monospace; font-size: 11px; }
    td.calc { font-size: 11px; color: #44403c; max-width: 220px; }
    td.empty { text-align: center; color: #78716c; padding: 24px; }
    .muted { font-family: sans-serif; font-size: 10px; color: #a8a29e; margin-top: 2px; }
    .exclusions { margin-top: 12px; padding: 8px 10px; background: #fffbeb; color: #92400e; font-size: 11px; border-radius: 6px; }
    .total { margin-top: 16px; padding-top: 12px; border-top: 2px solid #d6d3d1; display: flex; justify-content: space-between; font-size: 16px; font-weight: 700; }
    .footnote { margin-top: 10px; font-size: 10px; color: #a8a29e; }
    @media print {
      body { padding: 0; }
      @page { margin: 14mm; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>SRM Agri Warehouse LLP</h1>
    <div class="doc-title">${esc(title)}</div>
    ${billNumber ? `<div class="bill-no">${esc(billNumber)}</div>` : ""}
    ${status ? `<div class="status">${esc(status)}</div>` : ""}
  </div>
  <div class="meta">
    <div><div class="label">Party</div><div class="value">${esc(data.party_name)}${data.party_code ? ` (${esc(data.party_code)})` : ""}</div></div>
    <div><div class="label">Warehouse</div><div class="value">${esc(data.warehouse_name)}</div></div>
    <div><div class="label">Contract</div><div class="value">${esc(data.contract_number)} · ${esc(data.method)}</div></div>
    <div><div class="label">Period</div><div class="value">${esc(data.period_from)} to ${esc(data.period_to)}</div></div>
    ${data.tax_treatment ? `<div><div class="label">Tax</div><div class="value">${esc(data.tax_treatment)}</div></div>` : ""}
    ${data.posting_type ? `<div><div class="label">Posting</div><div class="value">${esc(data.posting_type)}</div></div>` : ""}
  </div>
  <table>
    <thead>
      <tr>
        <th>Period</th>
        <th>Lot / Stack</th>
        <th>Bag type</th>
        <th class="num">Bags/day</th>
        <th class="num">Days</th>
        <th class="num">Rent/day</th>
        <th>Calculation</th>
        <th class="num">Amount</th>
      </tr>
    </thead>
    <tbody>${lines}</tbody>
  </table>
  ${exclusions ? `<div class="exclusions"><strong>Already posted (excluded):</strong> ${exclusions}</div>` : ""}
  <div class="total"><span>Total</span><span>${esc(fmtMoney(data.amount))}</span></div>
  <p class="footnote">${esc(data.basis_label || "")}. Monthly rates use ×12 ÷365 days/year.</p>
  <script>
    window.onload = function() {
      window.focus();
      window.print();
      window.onafterprint = function() { window.close(); };
      setTimeout(function() { window.close(); }, 600);
    };
  </script>
</body>
</html>`;
}

/** Open a print-friendly window with the full rent bill / statement. */
export function printRentBill(data, options = {}) {
  if (!data) return false;
  const html = buildRentBillHtml(data, options);
  const win = window.open("", "_blank");
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  return true;
}
